import { useState, ChangeEvent, MouseEvent } from 'react';
import { Box, TextField, ToggleButton, ToggleButtonGroup, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  format,
  startOfDay, endOfDay, addDays, subDays,
  startOfWeek, endOfWeek, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
  startOfQuarter, endOfQuarter, addQuarters, subQuarters, getQuarter,
  startOfYear, endOfYear, addYears, subYears,
  getWeek, differenceInDays, isSameYear,
} from 'date-fns';
import { da } from 'date-fns/locale';
import type { Period, DateRange } from '../../types';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  showPeriodButtons?: boolean;
}

const MAX_RANGE_DAYS = 60;

const periodOptions: { value: Period; label: string }[] = [
  { value: 'day', label: 'Dag' },
  { value: 'week', label: 'Uge' },
  { value: 'month', label: 'Måned' },
  { value: 'quarter', label: 'Kvartal' },
  { value: 'year', label: 'År' },
];

export default function DateRangePicker({
  value,
  onChange,
  showPeriodButtons = true,
}: DateRangePickerProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null);
  const [rangeWarning, setRangeWarning] = useState('');

  const checkRange = (from: Date, to: Date): boolean => {
    const days = differenceInDays(to, from);
    if (days > MAX_RANGE_DAYS) {
      setRangeWarning(`Maks ${MAX_RANGE_DAYS} dage (valgt: ${days} dage)`);
      return false;
    }
    setRangeWarning('');
    return true;
  };

  const handlePeriodChange = (_: MouseEvent<HTMLElement>, period: Period | null) => {
    if (!period) {
      setSelectedPeriod(null);
      return;
    }

    setSelectedPeriod(period);
    const now = new Date();

    let from: Date;
    let to: Date;

    switch (period) {
      case 'day':
        from = startOfDay(now);
        to = endOfDay(now);
        break;
      case 'week':
        from = startOfWeek(now, { weekStartsOn: 1 });
        to = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        from = startOfMonth(now);
        to = endOfMonth(now);
        break;
      case 'quarter':
        from = startOfQuarter(now);
        to = endOfQuarter(now);
        break;
      case 'year':
        from = startOfYear(now);
        to = endOfYear(now);
        break;
      default:
        return;
    }

    if (checkRange(from, to)) {
      onChange({ from, to });
    }
  };

  const navigatePeriod = (direction: -1 | 1) => {
    const base = value.from;
    let from: Date;
    let to: Date;

    if (!selectedPeriod) {
      // Custom range: shift by the span of the current range
      const span = differenceInDays(value.to, value.from) + 1;
      from = startOfDay(direction === 1 ? addDays(base, span) : subDays(base, span));
      to = endOfDay(direction === 1 ? addDays(value.to, span) : subDays(value.to, span));
    } else {
      switch (selectedPeriod) {
        case 'day':
          from = startOfDay(direction === 1 ? addDays(base, 1) : subDays(base, 1));
          to = endOfDay(from);
          break;
        case 'week':
          from = startOfWeek(direction === 1 ? addWeeks(base, 1) : subWeeks(base, 1), { weekStartsOn: 1 });
          to = endOfWeek(from, { weekStartsOn: 1 });
          break;
        case 'month':
          from = startOfMonth(direction === 1 ? addMonths(base, 1) : subMonths(base, 1));
          to = endOfMonth(from);
          break;
        case 'quarter':
          from = startOfQuarter(direction === 1 ? addQuarters(base, 1) : subQuarters(base, 1));
          to = endOfQuarter(from);
          break;
        case 'year':
          from = startOfYear(direction === 1 ? addYears(base, 1) : subYears(base, 1));
          to = endOfYear(from);
          break;
        default:
          return;
      }
    }

    if (checkRange(from, to)) {
      onChange({ from, to });
    }
  };

  const getPeriodLabel = (): string => {
    const d = value.from;

    if (!selectedPeriod) {
      // Custom range: show "1. jan - 31. jan 2026" or "1. jan 2025 - 31. jan 2026"
      const fromLabel = format(d, 'd. MMM', { locale: da });
      const toLabel = format(value.to, 'd. MMM', { locale: da });
      if (isSameYear(d, value.to)) {
        return `${fromLabel} - ${toLabel} ${format(value.to, 'yyyy')}`;
      }
      return `${fromLabel} ${format(d, 'yyyy')} - ${toLabel} ${format(value.to, 'yyyy')}`;
    }

    switch (selectedPeriod) {
      case 'day': {
        const label = format(d, 'd. MMMM yyyy', { locale: da });
        return label.charAt(0).toUpperCase() + label.slice(1);
      }
      case 'week':
        return `Uge ${getWeek(d, { weekStartsOn: 1 })}, ${format(d, 'yyyy')}`;
      case 'month': {
        const label = format(d, 'MMMM yyyy', { locale: da });
        return label.charAt(0).toUpperCase() + label.slice(1);
      }
      case 'quarter':
        return `Q${getQuarter(d)} ${format(d, 'yyyy')}`;
      case 'year':
        return format(d, 'yyyy');
      default:
        return '';
    }
  };

  const handleFromChange = (e: ChangeEvent<HTMLInputElement>) => {
    const from = new Date(e.target.value);
    if (!isNaN(from.getTime())) {
      setSelectedPeriod(null);
      if (checkRange(startOfDay(from), value.to)) {
        onChange({ ...value, from: startOfDay(from) });
      }
    }
  };

  const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
    const to = new Date(e.target.value);
    if (!isNaN(to.getTime())) {
      setSelectedPeriod(null);
      if (checkRange(value.from, endOfDay(to))) {
        onChange({ ...value, to: endOfDay(to) });
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {showPeriodButtons && (
        <ToggleButtonGroup
          value={selectedPeriod}
          exclusive
          onChange={handlePeriodChange}
          size="small"
        >
          {periodOptions.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => navigatePeriod(-1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140, textAlign: 'center', whiteSpace: 'nowrap' }}>
          {getPeriodLabel()}
        </Typography>
        <IconButton size="small" onClick={() => navigatePeriod(1)}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          type="date"
          label="Fra"
          size="small"
          value={format(value.from, 'yyyy-MM-dd')}
          onChange={handleFromChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          type="date"
          label="Til"
          size="small"
          value={format(value.to, 'yyyy-MM-dd')}
          onChange={handleToChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
      </Box>
      {rangeWarning && (
        <Typography variant="caption" color="error" sx={{ mt: -0.5 }}>
          {rangeWarning}
        </Typography>
      )}
    </Box>
  );
}

// Helper function for other components - 30 days default to reduce API load
export function getDefaultDateRange(): DateRange {
  const now = new Date();
  return {
    from: startOfDay(subMonths(now, 1)),  // 30 days ago
    to: endOfDay(now),
  };
}

export function formatDateRange(range: DateRange): { from: string; to: string } {
  return {
    from: format(range.from, 'yyyy-MM-dd'),
    to: format(range.to, 'yyyy-MM-dd'),
  };
}
